import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Select } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, BankOutlined, GlobalOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.bgPrimary};
  padding: 20px;
`;

const RegisterCard = styled(Card)`
  width: 100%;
  max-width: 500px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background: ${COLORS.bgSecondary};
  border: 1px solid ${COLORS.border};

  .ant-card-body {
    padding: 40px;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${COLORS.textPrimary};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: ${COLORS.textSecondary};
  font-size: 14px;
  margin: 0;
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-weight: 600;
    color: ${COLORS.textPrimary} !important;
  }

  .ant-input,
  .ant-select-selector {
    background-color: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    border-radius: 8px;
    padding: 8px 12px;

    &:hover,
    &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }

  .ant-select-selector {
    height: 40px !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-search-input {
    color: ${COLORS.textPrimary} !important;
  }

  .ant-select-arrow {
    color: ${COLORS.textSecondary};
  }

  .ant-input-affix-wrapper {
    background-color: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    border-radius: 8px;
    padding: 8px 12px;

    &:hover,
    &:focus,
    &-focused {
      border-color: ${COLORS.accent} !important;
    }

    .ant-input {
      padding: 0;
    }

    .anticon {
      color: ${COLORS.textSecondary};
    }
  }

  .ant-btn-primary {
    height: 48px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 16px;
    background: ${COLORS.accent};
    border: none;
    color: ${COLORS.bgPrimary};

    &:hover {
      background: #67e8f9;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(34, 211, 238, 0.3);
    }
  }
`;

const NameRow = styled.div`
  display: flex;
  gap: 12px;

  .ant-form-item {
    flex: 1;
  }
`;

const FooterText = styled.div`
  text-align: center;
  margin-top: 16px;
  color: ${COLORS.textSecondary};
  font-size: 14px;

  a {
    color: ${COLORS.accent};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ResendContainer = styled.div`
  text-align: center;
  margin-top: 16px;

  .resend-link {
    color: ${COLORS.accent};
    cursor: pointer;
    font-size: 13px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 24px 0;

  h2 {
    color: ${COLORS.textPrimary};
    margin-bottom: 12px;
  }

  p {
    color: ${COLORS.textSecondary};
    margin-bottom: 8px;
    line-height: 1.6;
  }

  .email-highlight {
    color: ${COLORS.accent};
    font-weight: 600;
  }
`;

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await apiClient.post('/api/Public/Merchant/auth/register', {
        companyName: values.companyName,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        countryCode: values.countryCode,
      });

      setRegisteredEmail(values.email);
      setRegistered(true);
      message.success('Registration successful! Please check your email.');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!registeredEmail) return;
    setResendLoading(true);
    try {
      await apiClient.post('/api/Public/Merchant/auth/resend-registration-otp', {
        email: registeredEmail,
      });
      message.success('Verification email resent successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to resend email. Please try again.';
      message.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <PageContainer>
      <RegisterCard>
        <LogoContainer>
          <Logo
            src="/icon.svg"
            alt="Switch Wallet Logo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <Title>Create Account</Title>
          <Subtitle>Register your merchant company</Subtitle>
        </LogoContainer>

        {registered ? (
          <>
            <SuccessContainer>
              <h2>Check Your Email</h2>
              <p>
                We've sent a verification email to{' '}
                <span className="email-highlight">{registeredEmail}</span>
              </p>
              <p>
                Click the link in your email to set your password and complete registration.
              </p>
            </SuccessContainer>
            <ResendContainer>
              <span
                className="resend-link"
                onClick={!resendLoading ? handleResendOtp : undefined}
                style={{ opacity: resendLoading ? 0.5 : 1 }}
              >
                {resendLoading ? 'Sending...' : "Didn't receive the email? Resend"}
              </span>
            </ResendContainer>
            <FooterText style={{ marginTop: 24 }}>
              <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                Back to Login
              </a>
            </FooterText>
          </>
        ) : (
          <>
            <StyledForm
              form={form}
              name="register"
              onFinish={onFinish}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[
                  { required: true, message: 'Please enter your company name' },
                  { min: 2, message: 'Company name must be at least 2 characters' },
                ]}
              >
                <Input
                  prefix={<BankOutlined />}
                  placeholder="Enter company name"
                />
              </Form.Item>

              <NameRow>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[
                    { required: true, message: 'Required' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="First name"
                  />
                </Form.Item>

                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[
                    { required: true, message: 'Required' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Last name"
                  />
                </Form.Item>
              </NameRow>

              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Enter email address"
                />
              </Form.Item>

              <Form.Item
                name="phoneNumber"
                label="Phone Number"
                rules={[
                  { required: true, message: 'Please enter your phone number' },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Enter phone number"
                />
              </Form.Item>

              <Form.Item
                name="countryCode"
                label="Country"
                rules={[
                  { required: true, message: 'Please select your country' },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select country"
                  suffixIcon={<GlobalOutlined />}
                  optionFilterProp="label"
                  options={[
                    { value: 'NG', label: 'Nigeria' },
                    { value: 'US', label: 'United States' },
                    { value: 'GB', label: 'United Kingdom' },
                    { value: 'GH', label: 'Ghana' },
                    { value: 'KE', label: 'Kenya' },
                    { value: 'ZA', label: 'South Africa' },
                    { value: 'CA', label: 'Canada' },
                    { value: 'DE', label: 'Germany' },
                    { value: 'FR', label: 'France' },
                    { value: 'AE', label: 'United Arab Emirates' },
                    { value: 'SG', label: 'Singapore' },
                    { value: 'IN', label: 'India' },
                    { value: 'BR', label: 'Brazil' },
                    { value: 'AU', label: 'Australia' },
                  ]}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                >
                  Create Account
                </Button>
              </Form.Item>
            </StyledForm>

            <FooterText>
              Already have an account?{' '}
              <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                Sign In
              </a>
            </FooterText>
          </>
        )}
      </RegisterCard>
    </PageContainer>
  );
};

export default Register;
